import { inject } from '@angular/core'
import { performTsRestApiRequest } from '@ftr/api-shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { Loading, RemoteData, switchMapData } from '@ftr/foundation'
import { FeatureFlagKeyOfType, LaunchDarklyService } from '@ftr/ui-feature-flags'
import { map, Observable, of } from 'rxjs'
import {
  AnnotationsApiClient,
  AnnotationsApiClientFactory,
  AnnotationsApiClientFactoryV2,
  AnnotationsApiClientV2,
} from '../../api-client'

export abstract class AnnotationsService {
  launchDarklyService = inject(LaunchDarklyService)
  apiClientFactory = inject(AnnotationsApiClientFactory)
  regionCache = inject(CourtSystemRegionCache)
  apiClientFactoryV2 = inject(AnnotationsApiClientFactoryV2)

  protected performApiRequest<T, U>({
    courtSystemId,
    requestFn,
    responseMapFn,
    responseErrorMapFn = result => RemoteData.failure(result),
  }: {
    courtSystemId: string
    requestFn: (client: AnnotationsApiClient) => Promise<T>
    responseMapFn: (result: T) => RemoteData<U>
    responseErrorMapFn?: (result: Error) => RemoteData<U, Error>
  }): Observable<Loading | RemoteData<U, Error>> {
    return this.performApiRequestBase({
      client: this.getApiClient(courtSystemId),
      requestFn,
      responseMapFn,
      responseErrorMapFn,
    })
  }

  protected performApiRequestV2<T, U>({
    requestFn,
    responseMapFn,
    responseErrorMapFn = result => RemoteData.failure(result),
    courtSystemId,
  }: {
    courtSystemId: string
    requestFn: (client: AnnotationsApiClientV2) => Promise<T>
    responseMapFn: (result: T) => RemoteData<U>
    responseErrorMapFn?: (result: Error) => RemoteData<U, Error>
  }): Observable<Loading | RemoteData<U, Error>> {
    return this.performApiRequestBase({
      client: this.getApiClientV2(courtSystemId),
      requestFn,
      responseMapFn,
      responseErrorMapFn,
    })
  }

  private performApiRequestBase<T, U, C extends AnnotationsApiClient | AnnotationsApiClientV2>({
    client,
    requestFn,
    responseMapFn,
    responseErrorMapFn = result => RemoteData.failure(result),
  }: {
    client: Observable<C>
    requestFn: (client: C) => Promise<T>
    responseMapFn: (result: T) => RemoteData<U>
    responseErrorMapFn?: (result: Error) => RemoteData<U, Error>
  }): Observable<Loading | RemoteData<U, Error>> {
    return performTsRestApiRequest(client, { requestFn, responseMapFn, responseErrorMapFn })
  }

  protected getApiClient(courtSystemId: string): Observable<AnnotationsApiClient> {
    return this.regionCache
      .getOrFetchCourtSystem(of(courtSystemId))
      .pipe(map(({ region }) => this.apiClientFactory.build(region)))
  }

  protected getApiClientV2(courtSystemId: string): Observable<AnnotationsApiClientV2> {
    // TODO: remove once v2 is no longer optional
    const apiClientFactoryV2 = this.apiClientFactoryV2
    if (!apiClientFactoryV2) {
      throw new Error('V2 API client factory not provided')
    }
    return this.regionCache
      .getOrFetchCourtSystem(of(courtSystemId))
      .pipe(map(({ region }) => apiClientFactoryV2.build(region)))
  }

  /**
   * Runs an operation conditionally based on a feature flag.
   * If the flag is enabled, returns the provided value wrapped in an Observable.
   * Otherwise, runs the provided function
   * @param flag
   * @param value
   * @param func
   * @protected
   */
  protected runIfFeatureFlagEnabled<T>(
    flag: FeatureFlagKeyOfType<boolean>,
    value: Observable<RemoteData<T>>,
    func: Observable<Loading | RemoteData<T>>,
  ): Observable<Loading | RemoteData<T>> {
    const hasFlag = this.launchDarklyService.observeResolvedFlagChanges(flag)

    return hasFlag.pipe(
      switchMapData(enabled => {
        if (!enabled) {
          return value
        }

        return func
      }),
    )
  }
}
